import { type FC, useState } from "react"
import styled, { css } from "styled-components"
import tinycolor from "tinycolor2"
import Values from "values.js"

import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useVideos } from "~/api/video"
import Button from "~/themes/school/components/Button"
import Card, {
	CardHeader,
	CardSticker,
	CardText,
	CardThumbnail,
	CardTitle,
	Cards,
} from "~/themes/school/components/Card"
import { TitleSearch } from "~/themes/school/components/ContentSearch"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const VideosPage: FC = () => {
	const videos = useVideos()
	const t = useTranslation()

	const [searchItems, setSearchItems] = useState<number[]>()
	const [selectedTag, setSelectedTag] = useState<number>()

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("video.title")}</PageTitle>
				<TitleSearch
					items={videos.status === "success" ? videos.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{videos.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : videos.status === "loading" ? (
				<PartialLoadingPage />
			) : videos.status === "success" ? (
				<>
					{videos.tags.length > 0 && (
						<Tags>
							{videos.tags.map(tag => (
								<Tag
									key={tag.id}
									$isSelected={selectedTag === tag.id}
									onClick={() => setSelectedTag(current => (current !== tag.id ? tag.id : undefined))}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag !== undefined && (
								<Tag $isSelected onClick={() => setSelectedTag(undefined)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Cards>
						{(selectedTag !== undefined
							? videos.data.filter(video => video.tagIds.includes(selectedTag))
							: videos.data
						)
							.filter(x => searchItems?.includes(x.id) ?? true)
							.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
							.map(video => (
								<Card key={video.id} as={Button} onClick={routes.video.detail(video.id)}>
									<CardHeader>
										<CardTitle>{video.title}</CardTitle>
										<CardSticker>
											<Price>{video.priceLabel.value}</Price>
										</CardSticker>
									</CardHeader>
									<CardThumbnail filePath={video.thumbnailFilePath} />
									{video.shortDescription && <CardText>{video.shortDescription}</CardText>}
								</Card>
							))}
					</Cards>
				</>
			) : (
				exhaustiveGuard(videos.status)
			)}
		</Layout>
	)
}

const Tags = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 16px;

	@media (max-width: 999px) {
		gap: 8px;
		margin-left: 16px;
		margin-right: 16px;
	}
`

const Tag = styled(Button)<{ $isSelected: boolean }>(props => {
	const color = props.$isSelected
		? props.theme.primaryColor
		: new Values(props.theme.primaryColor).tint(50).hexString()

	return css`
		padding: 12px 24px;
		border-radius: 40px;
		background-color: ${color};
		color: ${tinycolor(color).isDark() ? "white" : "black"};
		line-height: 1;
	`
})

const Price = styled.div`
	font-weight: bold;
	font-size: 16px;
	color: #ffa412;
`

export default VideosPage
